// @flow
import { combineReducers } from 'redux';
import type { ReferralBonusActionType, ActivationBonusActionType, PromotionCodeActionType } from './actions';
import type { StateType } from '../../reducers';
import { isDiscountSelector, referralBonusesSelector, activationBonusAmountSelector } from '../../Bonuses';
import I18n from '../../../lib/i18n';
import { newOfferPriceSelector } from '../../NewOffer/selectors';
import type { GodchildBonusType } from 'digicel-kaizen-library';

const initialReferralState: ReferralBonusStateType = {
  godfatherExternalId: null,
  error: null,
};

const initialActivationState: ActivationBonusStateType = {
  isValid: false,
  isNotFirstSubscription: false,
};

const initialPromotionState: PromotionCodeStateType = {
  isModalVisible: false,
  promotion: null,
};

const referralReducer = (state: ReferralBonusStateType = initialReferralState, action: ReferralBonusActionType) => {
  switch (action.type) {
    case 'SIGNUP_SET_GODFATHER_EXTERNAL_ID':
      return {
        ...state,
        godfatherExternalId: action.payload.godfatherExternalId,
      };

    case 'SIGNUP_SET_GODFATHER_VALIDITY':
      return {
        ...state,
        error: action.payload.isValid ? false : action.payload.error ? action.payload.error : true,
      };

    default:
      return state;
  }
};

const activationReducer = (
  state: ActivationBonusStateType = initialActivationState,
  action: ActivationBonusActionType
) => {
  switch (action.type) {
    case 'SIGNUP_SET_ACTIVATION_BONUS_VALIDITY':
      return {
        ...state,
        isValid: action.payload.isActivationBonusValid,
      };
    case 'SIGNUP_SET_ACTIVATION_NOT_FIRST_LINE':
      return {
        ...state,
        isNotFirstSubscription: action.payload.isNotFirstSubscription,
      };
    default:
      return state;
  }
};

const promotionCodeReducer = (
  state: PromotionCodeStateType = initialPromotionState,
  action: PromotionCodeActionType
) => {
  switch (action.type) {
    case 'SIGNUP_SET_PROMOTION_MODAL_VISIBLE':
      return {
        ...state,
        isModalVisible: action.payload.isModalVisible,
      };
    case 'SIGNUP_SET_PROMOTION':
      return {
        ...state,
        promotion: action.payload.promotion,
      };
    default:
      return state;
  }
};

export default combineReducers({
  referral: referralReducer,
  activation: activationReducer,
  promotionCode: promotionCodeReducer,
});

const bonusStateSelector = (state: StateType): BonusStateType => state.signUp.bonus;

export const canDeleteABonusSelector = (state: StateType): boolean => {
  const multibonus =
    [
      promotionSelector(state),
      isGodfatherValid(state) && !!godfatherExternalIdSelector(state),
      isActivationBonusValidSelector(state),
    ].filter(Boolean).length >= 2;

  if (multibonus) {
    return true;
  } else {
    return !!promotionSelector(state) ? true : false;
  }
};

const referralBonusStateSelector = (state: StateType): ReferralBonusStateType => {
  const bonusState = bonusStateSelector(state);
  return bonusState.referral;
};

const activationBonusStateSelector = (state: StateType): ActivationBonusStateType => {
  const bonusState = bonusStateSelector(state);
  return bonusState.activation;
};

const promotionCodeStateSelector = (state: StateType): PromotionCodeStateType => {
  const bonusState = bonusStateSelector(state);
  return bonusState.promotionCode;
};

export const godfatherExternalIdSelector = (state: StateType): ?string => {
  const { godfatherExternalId } = referralBonusStateSelector(state);
  if (!godfatherExternalId) return null;
  return godfatherExternalId;
};

export const isGodfatherValid = (state: StateType): boolean => {
  const bonusState = referralBonusStateSelector(state);
  return !bonusState.error;
};

export const godfatherValidityErrorSelector = (state: StateType): ?string => {
  const bonusState = referralBonusStateSelector(state);
  const error = bonusState.error;

  if (error === true) {
    return I18n.t('SignUp.ConfirmPlan.referralPromotion.invalid.detail');
  }

  return bonusState.error;
};

export const godfatherExternalIdForOrderSimSelector = (state: StateType): ?string => {
  if (!isGodfatherValid(state)) {
    return null;
  }

  return godfatherExternalIdSelector(state);
};

export const godfatherExternalIdForActivateSimSelector = (state: StateType): ?string => {
  if (!isGodfatherValid(state)) {
    return null;
  }

  const selectedOfferPrice = newOfferPriceSelector(state);
  const godchildBonus = referralPromotionBonusSelector(state);
  const isDiscount = isDiscountSelector(state);

  if (godchildBonus?.price && isDiscount && selectedOfferPrice <= godchildBonus.price) {
    return null;
  }

  return godfatherExternalIdSelector(state);
};

export const referralPromotionBonusSelector = (state: StateType): ?GodchildBonusType => {
  if (!godfatherExternalIdSelector(state)) return;
  const referralBonuses = referralBonusesSelector(state);
  return referralBonuses?.godchild;
};

export const isActivationBonusValidSelector = (state: StateType): boolean => {
  const { isValid } = activationBonusStateSelector(state);
  return isValid;
};

export const isActivationNotFirstSubscriptionSelector = (state: StateType): boolean => {
  const { isNotFirstSubscription } = activationBonusStateSelector(state);
  return isNotFirstSubscription;
};

export const isPromotionCodeModalVisibleSelector = (state: StateType): boolean => {
  const { isModalVisible } = promotionCodeStateSelector(state);
  return isModalVisible;
};

export const promotionSelector = (state: StateType): ?PromotionType => {
  const { promotion } = promotionCodeStateSelector(state);
  return promotion;
};

export const promotionCodeSelector = (state: StateType): string => {
  const promotion = promotionSelector(state);
  return promotion ? promotion.code : '';
};

export const shouldShowGodfatherSelector = (state: StateType): boolean => {
  return (
    !promotionSelector(state) &&
    !!godfatherExternalIdSelector(state) &&
    (isGodfatherValid(state) || !isActivationBonusValidSelector(state))
  );
};

export const shouldBenefitFromActivationBonusSelector = (state: StateType): boolean => {
  return (
    !promotionSelector(state) &&
    isActivationBonusValidSelector(state) &&
    !!activationBonusAmountSelector(state) &&
    (!godfatherExternalIdSelector(state) || !isGodfatherValid(state))
  );
};

export type ReferralBonusStateType = {
  godfatherExternalId: ?string,
  error: ?string,
};

export type ActivationBonusStateType = {
  isValid: boolean,
  isNotFirstSubscription: boolean,
};

export type PromotionType = {
  code: string,
  incompatibilities: Array<string>,
  maxOccurences: number,
  price: number,
  productOffering: Object,
};

export type PromotionCodeStateType = {
  isModalVisible: boolean,
  promotion: ?PromotionType,
};

export type BonusStateType = {
  referral: ReferralBonusStateType,
  activation: ActivationBonusStateType,
  promotionCode: PromotionCodeStateType,
};
